import { useMemo } from 'react';

import { getServiceId } from '@/api/tokens.helper';
import { useAuth } from '@/store/auth/auth.store';
import { usePermissions as usePermissionsStore } from '@/store/permissions/permissions.store';

import type { Permission } from './permissions.catalog';

const EMPTY: ReadonlySet<string> = new Set();

/**
 * The active vessel's rights, or `vesselId`'s when a page shows another of yours: the base
 * rights and that vessel's own, composed as the session composes the active one's.
 */
export const usePermissions = (vesselId?: string) => {
  const activeRights = useAuth((s) => s.session?.rights);
  const services = useAuth((s) => s.session?.services);
  const base = usePermissionsStore((s) => s.basePermissions);
  const other = !!vesselId && vesselId !== getServiceId();

  return useMemo(() => {
    const set = other
      ? new Set([
          ...base,
          ...(services ?? [])
            .filter((entry) => entry.service_id === vesselId)
            .flatMap((entry) => entry.rights ?? []),
        ])
      : (activeRights ?? EMPTY);
    return {
      can: (p: Permission) => set.has(p),
      canAny: (ps: Permission[]) => ps.some((p) => set.has(p)),
      canAll: (ps: Permission[]) => ps.every((p) => set.has(p)),
      rights: set,
    };
  }, [other, vesselId, base, services, activeRights]);
};
